import * as React from "react";
import { ReactFlow, Background, BackgroundVariant, type Node, type Edge } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import nodeTypes from "./nodeTypes";
import DataEdge from "./edgeTypes/DataEdge";
import ExecEdge from "./edgeTypes/ExecEdge";
import "@flows/canvas/colors.css";
import type { StoryMeta } from "@features/devtools/registry/types";
import type { NodeTemplate } from "@features/flows/palette/registry";
import { inferTypes, applyInferredTypesToNodes } from "@features/flows/lib/type-infer";

type Props = {
  schemaKind?: "string" | "number" | "boolean" | "object" | "array";
  applyInference?: boolean;
};

function buildTemplates(): Map<string, NodeTemplate> {
  const map = new Map<string, NodeTemplate>();
  map.set("core.json.parse", {
    key: "core.json.parse",
    title: "JSON • Parse",
    category: "JSON",
    ports: {
      exec: { in: [{ id: "in", type: "any" }], out: [{ id: "next", type: "any" }] },
      data: { in: [{ id: "text", type: "string" }, { id: "schema", type: "object" }], out: [{ id: "value", type: "any" }] },
    },
    pkg: "core",
    typeLinks: [
      { from: { attr: "schema", op: "schemaType" }, to: { io: "out", id: "value" } },
    ],
  } as any);
  return map;
}

function JsonParsePreview({ schemaKind = "object", applyInference = true }: Props) {
  const templates = React.useMemo(buildTemplates, []);
  const schema = React.useMemo(() => {
    switch (schemaKind) {
      case "string": return { type: "string" };
      case "number": return { type: "number" };
      case "boolean": return { type: "boolean" };
      case "array": return { type: "array", items: { type: "string" } };
      case "object":
      default: return { type: "object", properties: { name: { type: "string" } } };
    }
  }, [schemaKind]);

  const baseNodes: Node[] = [
    {
      id: "parse",
      type: "default",
      position: { x: 120, y: 120 },
      data: {
        label: "JSON • Parse",
        templateKey: "core.json.parse",
        attrs: { schema },
        pins: {
          exec: { in: [{ id: "in" }], out: [{ id: "next" }] },
          data: { in: [{ id: "text", type: "string" }, { id: "schema", type: "object" }], out: [{ id: "value", type: "any" }] },
        },
      },
    },
    {
      id: "sink",
      type: "default",
      position: { x: 460, y: 120 },
      data: {
        label: "Sink",
        pins: {
          exec: { in: [{ id: "in" }], out: [{ id: "next" }] },
          data: { in: [{ id: "value", type: "any" }], out: [] },
        },
      },
    },
  ];
  const baseEdges: Edge[] = [
    {
      id: "e-val",
      type: "data",
      source: "parse",
      target: "sink",
      sourceHandle: "data:out:value",
      targetHandle: "data:in:value",
      data: { channel: "data", rawSourceHandle: "value", rawTargetHandle: "value" },
    } as any,
  ];
  const edgeTypes = React.useMemo(() => ({ data: DataEdge, exec: ExecEdge }), []);
  const [nodes, setNodes] = React.useState<Node[]>(baseNodes);
  const [edges, setEdges] = React.useState<Edge[]>(baseEdges);

  React.useEffect(() => {
    const n0 = [{ ...baseNodes[0], data: { ...(baseNodes[0].data as any), attrs: { schema } } } as Node, baseNodes[1]];
    if (!applyInference) { setNodes(n0); setEdges(baseEdges); return; }
    try {
      const inferred = inferTypes(n0 as any, baseEdges as any, templates);
      const nextNodes = applyInferredTypesToNodes(n0 as any, inferred) as any as Node[];
      setNodes(nextNodes);
      setEdges(baseEdges);
    } catch {
      setNodes(n0);
      setEdges(baseEdges);
    }
  }, [applyInference, schemaKind, templates]);

  return (
    <div className="h-[560px] border rounded">
      <ReactFlow<Node, Edge>
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        panOnScroll
        panOnDrag
        zoomOnScroll
      >
        <Background variant={BackgroundVariant.Dots} gap={24} size={1} />
      </ReactFlow>
    </div>
  );
}

const meta: StoryMeta<Props> = {
  id: "flows/JsonParse",
  title: "JSON • Parse",
  group: "Flows/Typing",
  component: JsonParsePreview as any,
  description: "Parses schema attr to TypeExpr; output adopts schema type.",
  defaultProps: { schemaKind: "object", applyInference: true },
  controls: {
    schemaKind: { type: "select", label: "Schema Kind", options: ["object", "array", "string", "number", "boolean"] },
    applyInference: { type: "boolean", label: "Apply Inference" },
  },
  variants: [
    { name: "Before (no inference)", props: { applyInference: false } },
    { name: "After (object)", props: { schemaKind: "object", applyInference: true } },
    { name: "After (array<string>)", props: { schemaKind: "array", applyInference: true } },
  ],
};

export default meta;

